import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FigmaChevronDownIcon } from '../icons';
import { ReactComponent as MagicStarIcon } from '../icons/magic-star-icon.svg';
import venueApiService from '../../services/venueApi';
import { formatDate, isPastBookingDate, parseDateOnly } from '../VenueDashboard/utils/dateUtils';

const PAST_EVENT_STATUSES = new Set(['confirmed', 'completed', 'paid', 'checked_in']);

const formatDisplayDate = (value) => {
  if (!value) return '';
  const parsed = parseDateOnly(value) || new Date(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return formatDate(parsed, { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatRating = (value) => {
  const rating = Number(value);
  if (!Number.isFinite(rating)) return null;
  const rounded = Math.round(rating * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const toPastEvent = (booking) => {
  const status = String(booking?.status || '').toLowerCase();
  if (!PAST_EVENT_STATUSES.has(status) || !isPastBookingDate(booking)) return null;
  const guests = Number(booking.guest_count ?? booking.headcount);
  const title = String(booking.event_name || booking.event_type || '').trim() || 'Event';
  return {
    id: booking.id || `${title}-${booking.booking_date}`,
    title,
    guests: Number.isFinite(guests) && guests > 0 ? guests : null,
    date: formatDisplayDate(booking.booking_date),
    sort: String(booking.booking_date || ''),
  };
};

const toReview = (row) => {
  const rating = Math.round(Number(row?.rating));
  if (!Number.isFinite(rating) || rating < 1) return null;
  return {
    id: row.id || `${row.event_name}-${row.submitted_at}`,
    rating: Math.min(5, rating),
    date: formatDisplayDate(row.submitted_at || row.booking_date),
    event: String(row.event_name || '').trim() || 'Event',
    text: String(row.comment || '').trim(),
    sort: String(row.submitted_at || row.booking_date || ''),
  };
};

const Stars = ({ rating }) => (
  <span className="vd-review-stars" aria-label={`${rating} stars`}>
    {Array.from({ length: rating }, (_, index) => (
      <MagicStarIcon key={index} width="20" height="20" aria-hidden="true" />
    ))}
  </span>
);

const CommunitySection = ({ title, open, onToggle, children }) => (
  <>
    <div className="amenity-divider" />
    <section className="vd-community-section">
      <div
        className="vd-acc-head flex items-center justify-between cursor-pointer"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex items-center py-8">
          <span className="vd-acc-title">{title}</span>
        </div>
        <FigmaChevronDownIcon className={`chevron-icon ${open ? 'expanded' : ''}`} width="14" height="14" />
      </div>
      {open ? <div className="vd-community-body">{children}</div> : null}
    </section>
  </>
);

const VenueCommunity = ({ images = [], rating, venueId, onReviewCountChange }) => {
  const [eventsOpen, setEventsOpen] = useState(true);
  const [reviewsOpen, setReviewsOpen] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [events, setEvents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const photos = images.map((image) => image.src).filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    onReviewCountChange?.(null);
    if (!venueId) {
      setEvents([]);
      setReviews([]);
      setAverageRating(null);
      setLoaded(true);
      return undefined;
    }

    (async () => {
      const [feedback, bookings] = await Promise.all([
        venueApiService.getVenueFeedback(venueId),
        venueApiService.getVenueBookings(venueId),
      ]);
      if (cancelled) return;
      const nextEvents = bookings
        .map(toPastEvent)
        .filter(Boolean)
        .sort((a, b) => (a.sort < b.sort ? 1 : a.sort > b.sort ? -1 : 0));
      const nextReviews = (feedback.feedback || [])
        .map(toReview)
        .filter(Boolean)
        .sort((a, b) => (a.sort < b.sort ? 1 : a.sort > b.sort ? -1 : 0));
      setEvents(nextEvents);
      setReviews(nextReviews);
      setAverageRating(feedback.average_rating);
      setLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [venueId, onReviewCountChange]);

  useEffect(() => {
    if (loaded) onReviewCountChange?.(reviews.length);
  }, [loaded, reviews.length, onReviewCountChange]);

  const reviewCount = reviews.length;
  const headingRating = reviewCount > 0
    ? (formatRating(averageRating) || formatRating(rating))
    : null;
  const reviewLabel = reviewCount === 1 ? '1 review' : `${reviewCount} reviews`;

  return (
    <div className="vd-community">
      <CommunitySection
        title="Past events"
        open={eventsOpen}
        onToggle={() => setEventsOpen((open) => !open)}
      >
        {loaded && events.length === 0 ? (
          <p className="vd-community-empty">No past event to show</p>
        ) : null}
        {events.length > 0 ? (
          <div className="vd-event-grid">
            {events.slice(0, 4).map((event, index) => (
              <article className="vd-event-card" key={event.id || `${event.title}-${index}`}>
                {photos.length > 0 ? (
                  <img src={photos[index % photos.length]} alt="" />
                ) : (
                  <div className="vd-event-card-fallback" />
                )}
                <div className="vd-event-copy">
                  <h4>{event.title}</h4>
                  <p className="vd-event-meta">
                    {event.guests != null ? (
                      <span>
                        <img src="/manual-booking/icons/pending-guests.png" alt="" width="20" height="20" />
                        {event.guests} {event.guests === 1 ? 'guest' : 'guests'}
                      </span>
                    ) : null}
                    {event.date ? (
                      <span>
                        <img src="/manual-booking/icons/pending-calendar.png" alt="" width="20" height="20" />
                        {event.date}
                      </span>
                    ) : null}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </CommunitySection>

      <CommunitySection
        title={(
          <span className="vd-reviews-heading">
            <MagicStarIcon width="20" height="20" />
            {headingRating ? <span>{headingRating}</span> : null}
            {headingRating ? <span className="vd-reviews-dot" aria-hidden="true">•</span> : null}
            <span>{loaded ? reviewLabel : 'Reviews'}</span>
          </span>
        )}
        open={reviewsOpen}
        onToggle={() => setReviewsOpen((open) => !open)}
      >
        {loaded && reviews.length === 0 ? (
          <p className="vd-community-empty">No reviews to show</p>
        ) : null}
        {reviews.map((review, index) => (
          <article className="vd-review" key={review.id || `${review.event}-${index}`}>
            <Stars rating={review.rating} />
            {review.date ? <p className="vd-review-date">{review.date}</p> : null}
            {review.text ? <p className="vd-review-text">{review.text}</p> : null}
            <p className="vd-review-event">Event: {review.event}</p>
          </article>
        ))}
      </CommunitySection>
    </div>
  );
};

CommunitySection.propTypes = {
  title: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

Stars.propTypes = {
  rating: PropTypes.number.isRequired,
};

VenueCommunity.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
  })),
  rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  venueId: PropTypes.string,
  onReviewCountChange: PropTypes.func,
};

export default VenueCommunity;
